import React from "react"
import {
  Flex,
  Text,
  Heading,
  Box,
  Link,
  Image,
  Button,
  Divider,
} from "@chakra-ui/react"

import { Link as GatsbyLink } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import { ALL_STAR_PAGES_URL } from "../../../constants/constants"
import { useYear } from "../../../context/YearContext"
import sectionData from "../../../../static/data/all_stars_page.json"

const styles = {
  root: {
    width: "100%",
    px: { base: "16px", md: "24px", lg: "24px" },
  },
  "inner-box": {
    margin: "0 auto",
    my: { base: "20px", md: "20px", lg: "60px" },
    width: "100%",
    flexDirection: { base: "column-reverse", md: "column-reverse", lg: "row" },
    alignItems: { base: "center", md: "center", lg: "flex-start" },
    justifyContent: { base: "flex-start", md: "flex-start", lg: "center" },
  },
  "left-box": {
    maxWidth: { base: "100%", md: "100%", lg: "576px" },
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    mr: { base: "0px", md: "0px", lg: "40px" },
  },
  "right-box": {
    mr: { base: "0", md: "0", lg: "89px" },
    maxWidth: { base: "100%", md: "100%", lg: "375px" },
  },
  "heading-box": {
    width: "100%",
    mt: { base: "40px", md: "40px", lg: "0px" },
  },
  heading: {
    fontSize: { base: "32px", md: "32px", lg: "40px" },
    fontWeight: 700,
    lineHeight: { base: "40px", md: "40px", lg: "48px" },
    textAlign: "center",
    letterSpacing: "0em",
  },
  greeting: {
    fontSize: { base: "24px", md: "24px", lg: "32px" },
    fontWeight: 700,
    lineHeight: { base: "32px", md: "32px", lg: "40px" },
    letterSpacing: "0em",
    color: "#129459",
  },
  "sub-heading": {
    mt: "8px",
    fontSize: { base: "18px", md: "18px", lg: "24px" },
    lineHeight: { base: "21.48px", md: "21.48px", lg: "32px" },
    fontWeight: 700,
  },
  text: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    color: "#2D3748",
    alignItems: "center",
  },
  link: {
    fontSize: "16px",
    color: "#3399FF",
    fontWeight: 700,
    lineHeight: "24px",
  },
  tag: {
    height: "32px",
    padding: "4px 16px 4px 16px",
    bg: "#ECC94B",
  },
  "tag-text": {
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "24px",
  },
  "allstar-text": {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "24px",
    color: "#129459",
  },
  hero: {
    mt: { base: "16px", md: "16px", lg: "40px" },
  },
  "page-links": {
    alignItems: "center",
    flexWrap: "wrap",
  },
  seperator: {
    marginRight: "16px",
    mb: "16px",
  },
  divider: {
    marginRight: "16px",
    mb: "16px",
    h: "24px",
    borderColor: "#CCCCCD",
  },
  box: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    color: "#2D3748",
    alignItems: "center",

    p: {
      marginTop: "24px",
    },

    ol: {
      marginTop: "24px",
      marginLeft: "16px",
    },
  },

  ".box-25": {
    div: {
      display: "flex",
      gap: "54px",
      marginTop: "24px",
      marginBottom: "24px",
    },
    p: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flexStart",
    },
    span: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },

  "promote-button": {
    flex: "1",
    minWidth: "210px",
    padding: "8px 16px",
    bg: "#ECC94B",
    borderRadius: "4px",
    color: "#000000",
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "24px",
    letterSpacing: "0em",
    "&:hover,&:active,&:focus": {
      bg: "#ECC94B",
      outline: "none",
      border: "none",
    },
  },
}

const SectionHero = ({ data: graphQLData }) => {
  const { selectedYear } = useYear()
  const data = sectionData[selectedYear] || sectionData["2025"]
  const badges = graphQLData?.allBadges?.edges || []

  const badgeImageName = sectionData[selectedYear]?.sectionHero?.image

  const badgeItem = badges?.find(item => item.node.name === badgeImageName)
  const styling = selectedYear === "2025" ? styles[".box-25"] : styles.box
  return (
    <Box sx={styles.root}>
      <Flex sx={styles["inner-box"]}>
        <Flex sx={styles["left-box"]}>
          <Box sx={styles["heading-box"]}>
            <Heading sx={styles["sub-heading"]}>
              {data.sectionHero.title}
            </Heading>
          </Box>

          <Text sx={styles.text} mt="24px">
            {data.sectionHero.subtitle}
          </Text>
          <Box
            dangerouslySetInnerHTML={{ __html: data.sectionHero.description }}
            sx={styling}
          />

          {selectedYear === "2025" && (
            <>
              <Link href="#specialties">
                <Button sx={styles["promote-button"]}>See the All-Stars</Button>
              </Link>
              <Divider mt={"24px"} borderColor="#6E7E99" />
            </>
          )}

          <Text sx={styles.text} mt="24px">
            For additional details on the lists, please review the study{" "}
            <Link
              color="#3399FF"
              as={GatsbyLink}
              to={"/" + ALL_STAR_PAGES_URL.METHODOLOGY + "/"}
            >
              Methodology
            </Link>{" "}
            and see answers to{" "}
            <Link
              color="#3399FF"
              as={GatsbyLink}
              to={"/" + ALL_STAR_PAGES_URL.FAQ + "/"}
            >
              Frequently Asked Questions
            </Link>
            .
          </Text>
        </Flex>
        <Box sx={styles["right-box"]}>
          <Flex flexDirection="column" alignItems="center">
            <Heading as="h2" sx={styles["allstar-text"]}>
              Healthcare Research All-Stars
            </Heading>
            <Heading as="h2" sx={styles.heading} mt="8px">
              {data.sectionHero.logoTitle}
            </Heading>

            <Box sx={styles.hero}>
              <Image
                as={GatsbyImage}
                alt={"Healthcare Research All-Stars Logo"}
                image={badgeItem?.node?.childImageSharp?.gatsbyImageData}
                w="213px"
              />
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  )
}

export default SectionHero
